import React from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Grid3x3, Layers, AlertTriangle, Filter, Sparkles, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { fmtUSD, truncWallet } from "@/lib/format";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "@/components/ui/select";

const CONF_COLOR = {
  High:   "text-emerald-300 bg-emerald-500/15 border-emerald-500/30",
  Medium: "text-amber-300 bg-amber-500/15 border-amber-500/30",
  Low:    "text-rose-300 bg-rose-500/15 border-rose-500/30",
};

export default function Overlap() {
  const [category, setCategory] = React.useState("__ALL__");

  const { data: intel, isLoading } = useQuery({
    queryKey: ["portfolio-intelligence", category],
    queryFn: async () => {
      const params = category !== "__ALL__" ? { category } : {};
      return (await api.get("/intel/portfolio-intelligence", { params })).data;
    },
    staleTime: 60_000,
  });

  const clusters = intel?.clusters || [];
  const conc = intel?.concentration_risk || {};
  const concShare = Math.round((conc.top3_share || 0) * 100);
  const concRiskLevel = concShare >= 40 ? "High" : concShare >= 20 ? "Medium" : "Low";

  return (
    <div className="space-y-6" data-testid="overlap-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
            Portfolio Intelligence
          </div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-zinc-100 tracking-tight">
            Where elite conviction clusters.
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Every card is a market where multiple top-scored traders converged on the same thesis.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Filter size={13} className="text-zinc-500" />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-8 w-56 bg-zinc-900 border-zinc-800 text-xs text-zinc-200"
                           data-testid="overlap-category-filter">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="__ALL__">All categories</SelectItem>
              {(intel?.categories || []).map((c) => (
                <SelectItem key={c.name} value={c.name}>
                  {c.name} <span className="text-zinc-500">({c.count})</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Concentration risk banner */}
      <section
        className={`border rounded-md p-4 ${
          concRiskLevel === "High" ? "border-rose-500/30 bg-rose-500/5" :
          concRiskLevel === "Medium" ? "border-amber-500/30 bg-amber-500/5" :
          "border-emerald-500/30 bg-emerald-500/5"
        }`}
        data-testid="concentration-risk"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle
              size={16}
              className={concRiskLevel === "High" ? "text-rose-300 mt-0.5" :
                          concRiskLevel === "Medium" ? "text-amber-300 mt-0.5" :
                          "text-emerald-300 mt-0.5"}
            />
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">
                Concentration risk
              </div>
              <h3 className="font-heading text-lg font-semibold text-zinc-100 mt-1">
                Top 3 clusters hold {concShare}% of tracked exposure
              </h3>
              <p className="text-xs text-zinc-400 mt-1 max-w-lg">
                {concRiskLevel === "High"
                  ? "Elite portfolios are highly correlated. A single thesis break could trigger cascading losses."
                  : concRiskLevel === "Medium"
                  ? "Moderate concentration. Watch the top clusters for shared risk factors."
                  : "Diversified conviction across many markets — low correlation risk."}
              </p>
            </div>
          </div>
          <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-heading ${CONF_COLOR[concRiskLevel]}`}>
            {concRiskLevel}
          </span>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <Stat label="Total exposure"       value={fmtUSD(conc.total_exposure)} />
          <Stat label="Top-3 combined"       value={fmtUSD(conc.top3_exposure)} />
          <Stat label="Traders tracked"     value={(intel?.top_traders || []).length} />
        </div>
      </section>

      {/* Cluster grid */}
      <section>
        <div className="flex items-center gap-2 mb-3">
          <Layers size={14} className="text-zinc-400" />
          <h2 className="font-heading font-semibold text-zinc-100">Conviction clusters</h2>
          <span className="text-xs text-zinc-500">
            {clusters.length} clusters · sorted by trader count × alignment
          </span>
        </div>

        {isLoading && (
          <div className="text-sm text-zinc-500 py-6 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Building clusters…
          </div>
        )}

        {!isLoading && clusters.length === 0 && (
          <div className="border border-zinc-800 rounded-md p-6 text-center text-sm text-zinc-500">
            <Grid3x3 size={20} className="mx-auto text-zinc-600 mb-2" />
            No clusters in this category yet.
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-3">
          {clusters.slice(0, 20).map((c) => (
            <ClusterCard key={c.market_id} cluster={c} />
          ))}
        </div>
      </section>
    </div>
  );
}

function ClusterCard({ cluster }) {
  const [thesis, setThesis] = React.useState(null);

  const thesisMut = useMutation({
    mutationFn: async () =>
      (await api.post("/intel/portfolio-intelligence/cluster-thesis", {
        market_id: cluster.market_id,
        trader_ids: cluster.trader_ids,
        shared_thesis_direction: cluster.shared_thesis_direction,
        n_traders: cluster.n_traders,
        share_supporting: cluster.share_supporting,
        total_exposure: cluster.total_exposure,
        concentration_share: cluster.concentration_share,
        category: cluster.category,
      })).data,
    onSuccess: (r) => setThesis(r.answer || null),
  });

  const share = Math.round((cluster.share_supporting || 0) * 100);
  const conc = Math.round((cluster.concentration_share || 0) * 100);

  return (
    <article
      className="border border-zinc-800 rounded-md p-4 hover:border-zinc-600 transition-colors"
      data-testid={`cluster-card-${cluster.market_id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to={`/app/markets/${cluster.market_id}`}
            className="block font-heading text-base font-semibold text-zinc-100 hover:underline line-clamp-2"
          >
            {cluster.market_title}
          </Link>
          <div className="text-[11px] text-zinc-500 mt-0.5">{cluster.category}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Direction</div>
          <div className="text-sm text-zinc-100 font-heading">
            {cluster.shared_thesis_direction}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-4 gap-2">
        <MiniStat label="Traders" value={cluster.n_traders} />
        <MiniStat label="Alignment" value={`${share}%`} />
        <MiniStat label="Exposure" value={fmtUSD(cluster.total_exposure)} />
        <MiniStat label="Concentration" value={`${conc}%`}
                  accent={conc >= 70 ? "rose" : conc >= 50 ? "amber" : "emerald"} />
      </div>

      {/* AI thesis */}
      <div className="mt-3 pt-3 border-t border-zinc-800/70">
        {!thesis && !thesisMut.isPending && (
          <button
            onClick={() => thesisMut.mutate()}
            data-testid={`cluster-thesis-btn-${cluster.market_id}`}
            className="text-xs inline-flex items-center gap-1 text-zinc-300 hover:text-zinc-100"
          >
            <Sparkles size={11} /> Explain this cluster
          </button>
        )}
        {thesisMut.isPending && (
          <div className="text-xs text-zinc-500 flex items-center gap-1">
            <Loader2 size={11} className="animate-spin" /> Reasoning…
          </div>
        )}
        {thesis && (
          <div className="space-y-2" data-testid={`cluster-thesis-${cluster.market_id}`}>
            <p className="text-xs text-zinc-200 leading-snug">
              {thesis.executive}
            </p>
            {thesis.evidence?.length > 0 && (
              <ul className="text-[11px] text-zinc-400 space-y-0.5 pl-2">
                {thesis.evidence.slice(0, 2).map((e, i) => (
                  <li key={i}>· {e}</li>
                ))}
              </ul>
            )}
            <div className="flex items-center gap-2 text-[10px] text-zinc-500">
              <span className={`inline-flex px-1.5 py-0.5 rounded border font-heading ${CONF_COLOR[thesis.confidence] || CONF_COLOR.Medium}`}>
                {thesis.confidence}
              </span>
              {thesis.recommended_investigation?.[0] && (
                <span>→ {thesis.recommended_investigation[0]}</span>
              )}
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-sm bg-zinc-900/40 border border-zinc-800 p-2">
      <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-sm font-mono-num text-zinc-100 mt-0.5">{value}</div>
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  const color =
    accent === "rose"    ? "text-rose-300" :
    accent === "amber"   ? "text-amber-300" :
    accent === "emerald" ? "text-emerald-300" : "text-zinc-100";
  const tips = {
    "Traders":       "How many top-scored traders converged on this market with the same directional thesis.",
    "Alignment":     "Percentage of the traders in this cluster who share the dominant direction. 100% = unanimous.",
    "Exposure":      "Combined dollar value of positions held by the traders in this cluster.",
    "Concentration": "How lopsided the cluster's capital is. High = one or two wallets dominate exposure. Low = broadly distributed conviction.",
  };
  return (
    <div className="rounded-sm bg-zinc-900/40 border border-zinc-800/70 px-1.5 py-1"
         title={tips[label]}>
      <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 cursor-help">
        {label}{tips[label] && <span className="text-zinc-600 text-[8px]">ⓘ</span>}
      </div>
      <div className={`text-sm font-mono-num mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}
